import html2canvas from "html2canvas";
import "./pdfExportCapture.scss";
import {
  BASE_CAPTURE_SCALE,
  EXPORT_CAPTURE_MIN_HEIGHT_PX,
  EXPORT_CAPTURE_WIDTH_PX,
  MAX_CANVAS_DIMENSION_PX,
  PAGE_FOOTER_CLEARANCE_MM,
  PAGE_HEADER_HEIGHT_MM,
  PAGE_HEIGHT_MM,
  PAGE_MARGIN_MM,
  PAGE_WIDTH_MM,
  PX_TO_MM,
  TARGET_CHUNK_HEIGHT_CSS_PX,
} from "./pdfExportConstants";

export interface CapturedBlock {
  id: string;
  canvas: HTMLCanvasElement;
  widthMm: number;
  heightMm: number;
  splittable: boolean;
  continueOnPage: boolean;
  headerRowTopPx: number;
  headerRowHeightPx: number;
}

export interface CapturedRow {
  id: string;
  blocks: CapturedBlock[];
}

const PDF_BLOCK_SELECTOR = "[data-pdf-block]";
const PDF_ROW_SELECTOR = "[data-pdf-row]";
const PDF_IGNORE_SELECTOR = "[data-pdf-ignore]";

function resolveSafeCaptureScale(element: HTMLElement): number {
  const widthPx = Math.max(element.scrollWidth, element.clientWidth, 1);
  const heightPx = Math.max(element.scrollHeight, element.clientHeight, 1);
  const largestDimensionPx = Math.max(widthPx, heightPx);
  const maxScale = MAX_CANVAS_DIMENSION_PX / largestDimensionPx;
  return Math.min(BASE_CAPTURE_SCALE, maxScale);
}

function measureHeaderRowRegion(element: HTMLElement, captureScale: number): { topPx: number; heightPx: number } {
  const thead = element.querySelector("thead");
  if (!thead) {
    return { topPx: 0, heightPx: 0 };
  }
  const elementRect = element.getBoundingClientRect();
  const theadRect = thead.getBoundingClientRect();
  return {
    topPx: (theadRect.top - elementRect.top) * captureScale,
    heightPx: theadRect.height * captureScale,
  };
}

function gridTrackCount(element: HTMLElement): number {
  const style = getComputedStyle(element);
  if (style.display !== "grid" && style.display !== "inline-grid") {
    return 0;
  }
  const template = style.gridTemplateColumns.trim();
  if (!template || template === "none") {
    return 0;
  }
  return template.split(/\s+/).filter(track => track !== "0px").length;
}

function resolveRowId(element: HTMLElement, blockId: string): string {
  const rowAncestor = element.closest<HTMLElement>(PDF_ROW_SELECTOR);
  if (!rowAncestor) {
    return blockId;
  }
  // A one-column row is already stacked. Keep each card on its own full-width PDF row.
  if (gridTrackCount(rowAncestor) === 1) {
    return blockId;
  }
  return rowAncestor.getAttribute("data-pdf-row") || blockId;
}

function isBrokenImage(candidate: Element): boolean {
  return candidate instanceof HTMLImageElement && candidate.complete && candidate.naturalWidth === 0;
}

function shouldIgnoreForCapture(candidate: Element): boolean {
  return candidate.matches(PDF_IGNORE_SELECTOR) || isBrokenImage(candidate);
}

function blankBrokenImages(root: HTMLElement): () => void {
  const images = Array.from(root.querySelectorAll<HTMLImageElement>("img")).filter(isBrokenImage);
  for (const image of images) {
    image.dataset.pdfBrokenSrc = image.src;
    image.removeAttribute("src");
  }
  return () => {
    for (const image of images) {
      const originalSrc = image.dataset.pdfBrokenSrc;
      if (originalSrc !== undefined) {
        image.src = originalSrc;
        delete image.dataset.pdfBrokenSrc;
      }
    }
  };
}


async function replaceElementsWithSnapshots<T extends Element>(
  root: HTMLElement,
  findCandidates: (_root: HTMLElement) => T[],
  toPlaceholder: (_element: T) => Promise<HTMLImageElement | null> | HTMLImageElement | null,
): Promise<() => void> {
  const swaps: Array<{ original: T; placeholder: HTMLImageElement }> = [];

  for (const original of findCandidates(root)) {
    try {
      // eslint-disable-next-line no-await-in-loop -- each element is rasterized independently but cheaply; sequential keeps restoration order simple.
      const placeholder = await toPlaceholder(original);
      if (!placeholder) {
        continue;
      }
      original.replaceWith(placeholder);
      swaps.push({ original, placeholder });
    } catch {
      continue;
    }
  }

  return () => {
    for (const { original, placeholder } of swaps) {
      placeholder.replaceWith(original);
    }
  };
}


function matchRenderedBox(placeholder: HTMLImageElement, source: Element, widthPx: number, heightPx: number): void {
  placeholder.width = widthPx;
  placeholder.height = heightPx;
  const sourceStyle = getComputedStyle(source);
  placeholder.style.cssText = source.getAttribute("style") ?? "";
  placeholder.style.width = sourceStyle.width;
  placeholder.style.height = sourceStyle.height;
  placeholder.style.display = sourceStyle.display;
  placeholder.style.position = sourceStyle.position;
  placeholder.style.margin = sourceStyle.margin;
}

async function svgToImage(svg: SVGSVGElement): Promise<HTMLImageElement | null> {
  const rect = svg.getBoundingClientRect();
  if (rect.width === 0) {
    return null;
  }
  const widthPx = Math.max(1, Math.round(rect.width * BASE_CAPTURE_SCALE));
  const heightPx = Math.max(1, Math.round(rect.height * BASE_CAPTURE_SCALE));

  const serialized = new XMLSerializer().serializeToString(svg);
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;

  const sourceImage = await new Promise<HTMLImageElement | null>(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = svgDataUrl;
  });
  if (!sourceImage) {
    return null;
  }

  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.drawImage(sourceImage, 0, 0, widthPx, heightPx);

  const placeholder = document.createElement("img");
  placeholder.src = canvas.toDataURL("image/png");
  matchRenderedBox(placeholder, svg, widthPx, heightPx);
  return placeholder;
}

function replaceSvgsWithSnapshots(root: HTMLElement): Promise<() => void> {
  return replaceElementsWithSnapshots(
    root,
    element => Array.from(element.querySelectorAll<SVGSVGElement>("svg")),
    svgToImage,
  );
}

function canvasToImage(canvas: HTMLCanvasElement): HTMLImageElement | null {
  if (canvas.width === 0 || canvas.height === 0) {
    return null;
  }
  const placeholder = document.createElement("img");
  placeholder.src = canvas.toDataURL("image/png");
  matchRenderedBox(placeholder, canvas, canvas.width, canvas.height);
  return placeholder;
}

function replaceCanvasesWithSnapshots(root: HTMLElement): Promise<() => void> {
  return replaceElementsWithSnapshots(
    root,
    element => Array.from(element.querySelectorAll<HTMLCanvasElement>("canvas")),
    canvasToImage,
  );
}

const PAGE_CONTENT_WIDTH_MM = PAGE_WIDTH_MM - PAGE_MARGIN_MM * 2;
const PAGE_CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - PAGE_MARGIN_MM * 2 - PAGE_HEADER_HEIGHT_MM - PAGE_FOOTER_CLEARANCE_MM;
const CHUNK_HEIGHT_SAFETY_CSS_PX = 16;
const SKIP_ROW_CLASS = "pdf-export-skip-row";

interface TableMetrics {
  width: number;
  height: number;
  theadTop: number;
  theadBottom: number;
  rows: Array<{ top: number; bottom: number }>;
}

let lastTableMetrics: TableMetrics | null = null;

function readTableMetrics(cloned: HTMLElement): TableMetrics {
  const root = cloned.getBoundingClientRect();
  const table = cloned.querySelector("table");
  const thead = table?.tHead?.getBoundingClientRect();
  const bodyRows = table
    ? Array.from(table.querySelectorAll<HTMLTableRowElement>("tbody > tr"))
    : [];
  return {
    width: root.width,
    height: root.height,
    theadTop: thead ? thead.top - root.top : 0,
    theadBottom: thead ? thead.bottom - root.top : 0,
    rows: bodyRows.map(row => {
      const rect = row.getBoundingClientRect();
      return { top: rect.top - root.top, bottom: rect.bottom - root.top };
    }).filter(row => row.bottom - row.top > 0.5),
  };
}

function maxPageCssHeight(captureWidthCssPx: number): number {
  const widthPx = Math.max(captureWidthCssPx, 1);
  const widthMm = widthPx * PX_TO_MM;
  const widthScale = widthMm > PAGE_CONTENT_WIDTH_MM ? PAGE_CONTENT_WIDTH_MM / widthMm : 1;
  return Math.max(1, PAGE_CONTENT_HEIGHT_MM / (PX_TO_MM * widthScale) - CHUNK_HEIGHT_SAFETY_CSS_PX);
}

function cropCanvasRange(source: HTMLCanvasElement, startPx: number, endPx: number): HTMLCanvasElement {
  const top = Math.max(0, Math.min(source.height, Math.round(startPx)));
  const bottom = Math.max(top + 1, Math.min(source.height, Math.round(endPx)));
  if (top === 0 && bottom === source.height) {
    return source;
  }
  const cropped = document.createElement("canvas");
  cropped.width = source.width;
  cropped.height = bottom - top;
  const ctx = cropped.getContext("2d");
  if (ctx) {
    ctx.drawImage(source, 0, top, source.width, cropped.height, 0, 0, source.width, cropped.height);
  }
  return cropped;
}

function composeTableSlice(
  source: HTMLCanvasElement,
  pxPerCss: number,
  theadTop: number,
  theadBottom: number,
  bodyTop: number,
  bodyBottom: number,
): HTMLCanvasElement {
  const theadHeightPx = Math.max(0, Math.round((theadBottom - theadTop) * pxPerCss));
  const bodyHeightPx = Math.max(1, Math.round((bodyBottom - bodyTop) * pxPerCss));
  const sliced = document.createElement("canvas");
  sliced.width = source.width;
  sliced.height = theadHeightPx + bodyHeightPx;
  const ctx = sliced.getContext("2d");
  if (!ctx) {
    return sliced;
  }
  if (theadHeightPx > 0) {
    ctx.drawImage(
      source,
      0, Math.round(theadTop * pxPerCss), source.width, theadHeightPx,
      0, 0, source.width, theadHeightPx,
    );
  }
  ctx.drawImage(
    source,
    0, Math.round(bodyTop * pxPerCss), source.width, bodyHeightPx,
    0, theadHeightPx, source.width, bodyHeightPx,
  );
  return sliced;
}

interface TableSlice {
  canvas: HTMLCanvasElement;
  from: number;
  to: number;
  filled: boolean;
}

function splitCanvasAtRows(
  source: HTMLCanvasElement,
  metrics: TableMetrics | null,
  options: { continuation: boolean; includeTail: boolean },
): TableSlice[] {
  if (!metrics || metrics.rows.length === 0 || source.height === 0) {
    return [{ canvas: source, from: 0, to: 0, filled: true }];
  }
  const totalCss = Math.max(metrics.height, source.height / BASE_CAPTURE_SCALE);
  const pxPerCss = source.height / totalCss;
  const budgetCss = maxPageCssHeight(metrics.width);
  const tailCss = Math.max(0, totalCss - metrics.rows[metrics.rows.length - 1].bottom);
  const slices: TableSlice[] = [];
  let index = 0;

  const pushContinuation = (from: number, to: number, filled: boolean, withTail: boolean) => {
    const bodyBottom = withTail ? totalCss : metrics.rows[to].bottom;
    slices.push({
      canvas: composeTableSlice(
        source,
        pxPerCss,
        metrics.theadTop,
        metrics.theadBottom,
        metrics.rows[from].top,
        bodyBottom,
      ),
      from,
      to,
      filled,
    });
  };

  while (index < metrics.rows.length) {
    const isOpening = slices.length === 0 && !options.continuation;
    if (isOpening) {
      let last = index;
      while (last + 1 < metrics.rows.length && metrics.rows[last + 1].bottom <= budgetCss) {
        last += 1;
      }
      const isEnd = last === metrics.rows.length - 1;
      const withTail = isEnd && options.includeTail;
      const endCss = withTail ? totalCss : metrics.rows[last].bottom;
      const filled = !isEnd && metrics.rows[last].bottom <= budgetCss && last + 1 < metrics.rows.length
        && metrics.rows[last + 1].bottom > budgetCss;
      slices.push({
        canvas: cropCanvasRange(source, 0, endCss * pxPerCss),
        from: index,
        to: last,
        filled,
      });
      index = last + 1;
      continue;
    }

    const theadHeight = Math.max(0, metrics.theadBottom - metrics.theadTop);
    let used = theadHeight;
    let last = index;
    let cursor = index;
    let stoppedForBudget = false;
    while (cursor < metrics.rows.length) {
      const rowHeight = metrics.rows[cursor].bottom - metrics.rows[cursor].top;
      const isEndRow = cursor === metrics.rows.length - 1;
      const tail = isEndRow && options.includeTail ? tailCss : 0;
      if (cursor > index && used + rowHeight + tail > budgetCss) {
        stoppedForBudget = true;
        break;
      }
      used += rowHeight + tail;
      last = cursor;
      cursor += 1;
      if (used > budgetCss) {
        stoppedForBudget = cursor < metrics.rows.length;
        break;
      }
    }
    const isEnd = last === metrics.rows.length - 1;
    pushContinuation(index, last, stoppedForBudget && !isEnd, isEnd && options.includeTail);
    index = last + 1;
  }

  return slices.length > 0 ? slices : [{ canvas: source, from: 0, to: 0, filled: true }];
}

function cellContentWidth(cell: HTMLTableCellElement): number {
  const range = document.createRange();
  range.selectNodeContents(cell);
  return Math.max(cell.getBoundingClientRect().width, range.getBoundingClientRect().width);
}

function customersTableContentWidth(table: HTMLTableElement): number {
  const rows = [
    table.tHead?.rows[0],
    table.tBodies[0]?.rows[0],
  ].filter((row): row is HTMLTableRowElement => Boolean(row));
  if (rows.length === 0) {
    return table.scrollWidth;
  }
  const columnCount = Math.max(...rows.map(row => row.cells.length));
  let total = 0;
  for (let index = 0; index < columnCount; index += 1) {
    const widest = rows.reduce((width, row) => {
      const cell = row.cells[index];
      return cell ? Math.max(width, cellContentWidth(cell)) : width;
    }, 0);
    total += widest;
  }
  return Math.max(total, table.scrollWidth);
}

function captureElement(element: HTMLElement, scale: number): Promise<HTMLCanvasElement> {
  const customersTable = element.getAttribute("data-pdf-block") === "customers-table"
    ? element.querySelector("table")
    : null;
  const rect = element.getBoundingClientRect();
  const contentWidthPx = customersTable
    ? Math.ceil(Math.max(rect.width, element.scrollWidth, customersTableContentWidth(customersTable)) + 24)
    : 0;
  const captureFullWidth = customersTable !== null && contentWidthPx > element.clientWidth + 1;
  const exportWindowWidth = captureFullWidth
    ? Math.ceil(Math.max(EXPORT_CAPTURE_WIDTH_PX, contentWidthPx))
    : EXPORT_CAPTURE_WIDTH_PX;
  const exportWindowHeight = Math.max(
    EXPORT_CAPTURE_MIN_HEIGHT_PX,
    Math.ceil(element.scrollHeight),
  );

  return html2canvas(element, {
    scale,
    backgroundColor: "#ffffff",
    useCORS: true,
    ignoreElements: shouldIgnoreForCapture,
    windowWidth: exportWindowWidth,
    windowHeight: exportWindowHeight,
    scrollX: 0,
    scrollY: 0,
    ...(captureFullWidth
      ? {
          width: contentWidthPx,
          onclone: (_clonedDocument: Document, clonedElement: HTMLElement) => {
            clonedElement.style.setProperty("width", `${contentWidthPx}px`);
            clonedElement.style.setProperty("max-width", "none");
            clonedElement.style.setProperty("overflow", "visible");
            const clonedTable = clonedElement.querySelector("table");
            if (clonedTable) {
              clonedTable.style.setProperty("width", `${contentWidthPx}px`);
              clonedTable.style.setProperty("min-width", `${contentWidthPx}px`);
              clonedTable.style.setProperty("max-width", "none");
            }
            lastTableMetrics = readTableMetrics(clonedElement);
          },
        }
      : {
          onclone: (_clonedDocument: Document, clonedElement: HTMLElement) => {
            lastTableMetrics = readTableMetrics(clonedElement);
          },
        }),
  });
}

function toCapturedBlock(
  id: string,
  canvas: HTMLCanvasElement,
  splittable: boolean,
  headerRowTopPx: number,
  headerRowHeightPx: number,
  captureScale: number,
  continueOnPage = false,
): CapturedBlock {
  const cssPxToMm = PX_TO_MM / Math.max(captureScale, 1);
  return {
    id,
    canvas,
    widthMm: canvas.width * cssPxToMm,
    heightMm: canvas.height * cssPxToMm,
    splittable,
    continueOnPage,
    headerRowTopPx,
    headerRowHeightPx,
  };
}

async function captureRowBatch(
  element: HTMLElement,
  rows: HTMLTableRowElement[],
  chunkRows: HTMLTableRowElement[],
): Promise<HTMLCanvasElement> {
  const hiddenRows = rows.filter(row => !chunkRows.includes(row));
  for (const row of hiddenRows) {
    row.classList.add(SKIP_ROW_CLASS);
  }
  try {
    return await captureElement(element, BASE_CAPTURE_SCALE);
  } finally {
    for (const row of hiddenRows) {
      row.classList.remove(SKIP_ROW_CLASS);
    }
  }
}

async function captureTableInChunks(
  element: HTMLElement,
  table: HTMLTableElement,
): Promise<HTMLCanvasElement[]> {
  const tbody = table.querySelector("tbody");
  const rows = tbody ? Array.from(tbody.querySelectorAll<HTMLTableRowElement>(":scope > tr")) : [];
  if (!tbody || rows.length === 0) {
    return [];
  }

  const maxBatchCssPx = Math.min(
    TARGET_CHUNK_HEIGHT_CSS_PX * 0.5,
    MAX_CANVAS_DIMENSION_PX / BASE_CAPTURE_SCALE - 32,
  );
  const canvases: HTMLCanvasElement[] = [];
  let start = 0;
  let rowsPerPage = 0;
  let continuation = false;
  while (start < rows.length) {
    const batchCount = rowsPerPage > 0
      ? rowsPerPage + 3
      : Math.max(8, Math.floor(maxBatchCssPx / 48));
    const end = Math.min(rows.length, start + batchCount);
    const isLast = end === rows.length;
    const chunkRows = rows.slice(start, end);
    // eslint-disable-next-line no-await-in-loop -- chunks must render sequentially; html2canvas mutates a shared offscreen render context.
    const canvas = await captureRowBatch(element, rows, chunkRows);
    const slices = splitCanvasAtRows(canvas, lastTableMetrics, {
      continuation,
      includeTail: isLast,
    });
    const withholdLast = !isLast && slices.length > 0 && !slices[slices.length - 1].filled;
    const emit = withholdLast ? slices.slice(0, -1) : slices;
    if (emit.length === 0) {
      if (isLast) {
        canvases.push(slices[0].canvas);
        break;
      }
      rowsPerPage = Math.max(rowsPerPage + 1, end - start);
      continue;
    }
    canvases.push(...emit.map(slice => slice.canvas));
    const consumed = emit.reduce((count, slice) => count + (slice.to - slice.from + 1), 0);
    start += Math.max(1, consumed);
    const sample = [...emit].reverse().find(slice => slice.filled);
    if (sample) {
      rowsPerPage = sample.to - sample.from + 1;
    }
    continuation = true;
  }
  return canvases;
}

function isScrollContainer(element: HTMLElement): boolean {
  const { overflow, overflowX, overflowY } = getComputedStyle(element);
  return [overflow, overflowX, overflowY].some(value => value === "auto" || value === "scroll");
}

function findOverflowingElements(root: HTMLElement): HTMLElement[] {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>("*"));
  candidates.unshift(root);
  return candidates.filter(
    element => isScrollContainer(element) &&
      (element.scrollWidth > element.clientWidth + 1 ||
        element.scrollHeight > element.clientHeight + 1),
  );
}

function expandOverflowForCapture(root: HTMLElement): () => void {
  const elements = findOverflowingElements(root);
  for (const element of elements) {
    element.classList.add("pdf-export-unclip");
    if (element.scrollWidth > element.clientWidth + 1) {
      element.classList.add("pdf-export-unclip-x");
    }
  }

  return () => {
    for (const element of elements) {
      element.classList.remove("pdf-export-unclip", "pdf-export-unclip-x");
    }
  };
}

export async function captureBlocks(container: HTMLElement): Promise<CapturedRow[]> {
  const blockElements = Array.from(
    container.querySelectorAll<HTMLElement>(PDF_BLOCK_SELECTOR),
  );

  const rowsById = new Map<string, CapturedRow>();
  const rowOrder: string[] = [];

  for (const element of blockElements) {
    const id = element.getAttribute("data-pdf-block");
    if (!id) {
      continue;
    }
    const isSplittable = element.hasAttribute("data-pdf-splittable");
    const table = element.querySelector<HTMLTableElement>("table");

    const bodyRowCount = table?.querySelectorAll("tbody > tr").length ?? 0;
    const needsChunking = isSplittable && table !== null && bodyRowCount > 0;
    const restoreOverflow = expandOverflowForCapture(element);
    const restoreBrokenImages = blankBrokenImages(element);
    // eslint-disable-next-line no-await-in-loop -- must resolve before this element's html2canvas call runs.
    const restoreCanvases = await replaceCanvasesWithSnapshots(element);
    // eslint-disable-next-line no-await-in-loop -- must resolve before this element's html2canvas call runs.
    const restoreSvgs = await replaceSvgsWithSnapshots(element);

    let newCapturedBlocks: CapturedBlock[];
    try {
      if (needsChunking && table) {
        // eslint-disable-next-line no-await-in-loop -- chunks must be captured sequentially.
        const chunkCanvases = await captureTableInChunks(element, table);
        newCapturedBlocks = chunkCanvases.map((canvas, chunkIndex) => toCapturedBlock(
          chunkCanvases.length > 1 ? `${id}::page-${chunkIndex}` : id,
          canvas,
          false,
          0,
          0,
          BASE_CAPTURE_SCALE,
        ));
      } else {
        const captureScale = resolveSafeCaptureScale(element);
        const headerRegion = measureHeaderRowRegion(element, captureScale);
        // eslint-disable-next-line no-await-in-loop -- captures must run sequentially; html2canvas mutates a shared offscreen render context.
        const canvas = await captureElement(element, captureScale);
        newCapturedBlocks = [
          toCapturedBlock(
            id,
            canvas,
            isSplittable,
            headerRegion.topPx,
            headerRegion.heightPx,
            captureScale,
            element.hasAttribute("data-pdf-continue"),
          ),
        ];
      }
    } finally {
      restoreSvgs();
      restoreBrokenImages();
      restoreCanvases();
      restoreOverflow();
    }

    if (needsChunking && newCapturedBlocks.length > 1) {
      for (const chunkBlock of newCapturedBlocks) {
        rowsById.set(chunkBlock.id, { id: chunkBlock.id, blocks: [chunkBlock] });
        rowOrder.push(chunkBlock.id);
      }
      continue;
    }

    const rowId = resolveRowId(element, id);
    const existingRow = rowsById.get(rowId);
    if (existingRow) {
      existingRow.blocks.push(...newCapturedBlocks);
    } else {
      rowsById.set(rowId, { id: rowId, blocks: newCapturedBlocks });
      rowOrder.push(rowId);
    }
  }

  return rowOrder.map(rowId => rowsById.get(rowId)!);
}
